import React from 'react'

const CHART_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#6366f1',
  '#14b8a6', '#f472b6', '#a855f7', '#22c55e', '#fb923c'
]

interface PieChartData {
  label: string
  value: number
  color?: string
}

interface PieChartProps {
  data: PieChartData[]
  title?: string
  size?: number
  showLegend?: boolean
  formatValue?: (value: number) => string
}

export const PieChart: React.FC<PieChartProps> = ({
  data,
  title,
  size = 200,
  showLegend = true,
  formatValue = (value) => value.toString()
}) => {
  const radius = size / 2 - 20
  const centerX = size / 2
  const centerY = size / 2
  
  const total = data.reduce((sum, item) => sum + item.value, 0)
  
  if (total === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        {title && (
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            color: '#111827',
            fontFamily: 'Lexend Deca, sans-serif',
            marginBottom: '16px'
          }}>
            {title}
          </h3>
        )}
        <p style={{ color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif' }}>
          No data to display
        </p>
      </div>
    )
  }

  let currentAngle = 0
  const segments = data.map((item, index) => {
    const percentage = (item.value / total) * 100
    const angle = (item.value / total) * 2 * Math.PI
    const startAngle = currentAngle
    const endAngle = currentAngle + angle
    
    const x1 = centerX + radius * Math.cos(startAngle)
    const y1 = centerY + radius * Math.sin(startAngle)
    const x2 = centerX + radius * Math.cos(endAngle)
    const y2 = centerY + radius * Math.sin(endAngle)
    
    const largeArc = angle > Math.PI ? 1 : 0
    
    const pathData = [
      `M ${centerX} ${centerY}`,
      `L ${x1} ${y1}`,
      `A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`,
      'Z'
    ].join(' ')
    
    currentAngle += angle
    
    return {
      pathData,
      percentage,
      color: item.color || CHART_COLORS[index % CHART_COLORS.length],
      label: item.label,
      value: item.value
    }
  })

  return (
    <div>
      {title && (
        <h3 style={{ 
          fontSize: '16px', 
          fontWeight: '600', 
          color: '#111827',
          fontFamily: 'Lexend Deca, sans-serif',
          marginBottom: '16px',
          textAlign: 'center'
        }}>
          {title}
        </h3>
      )}
      
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {segments.map((segment, index) => (
            <path
              key={index}
              d={segment.pathData}
              fill={segment.color}
              stroke="white"
              strokeWidth="2"
            />
          ))}
        </svg>
        
        {showLegend && (
          <div style={{ flex: 1, minWidth: '200px' }}>
            {segments.map((segment, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontFamily: 'Lexend Deca, sans-serif'
                }}
              >
                <div
                  style={{
                    width: '12px',
                    height: '12px',
                    backgroundColor: segment.color,
                    borderRadius: '2px',
                    minWidth: '12px'
                  }}
                />
                <span style={{ color: '#374151', flex: 1 }}>
                  {segment.label}
                </span>
                <span style={{ color: '#6b7280', minWidth: '60px', textAlign: 'right' }}>
                  {formatValue(segment.value)} ({segment.percentage.toFixed(1)}%)
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}